import {
  RECHARGE_FEATURE,
  RECHARGE_FEATURE_MODE,
  RECHARGE_FEATURE_MODES,
  RENEWAL_DISCOUNT_FEATURE_MODES,
  RECHARGE_FEATURES,
  type RechargeFeature,
  type RechargeFeatureMode,
  type RechargeFeatureSettings,
  type RechargeSettingsSummary,
  type SwissCantonFormat,
} from '@flux/shared';
import { Info } from 'lucide-react';
import { useState, type FormEvent, type JSX } from 'react';
import { PageSection } from '@/components/layout/page.js';
import { ConfirmDialog } from '@/components/ui/confirm-dialog.js';
import { Input } from '@/components/ui/input.js';
import { Label } from '@/components/ui/label.js';
import { SaveButton } from '@/components/ui/save-button.js';
import { isSameValue } from '@/lib/is-same-value.js';
import { describedByIds, FieldMessages, SegmentedRadioGroup, type SegmentedOption } from './recharge-parts.js';
import { useSaveRechargeSettings } from './use-save-recharge-settings.js';

interface FeaturesDraft {
  chargeFailed: { mode: RechargeFeatureMode; maxAttempts: string };
  swissCanton: { mode: RechargeFeatureMode; provinceFormat: SwissCantonFormat };
  legacyMigration: { mode: RechargeFeatureMode };
  renewalDiscount: { mode: RechargeFeatureMode; reconcileSchedule: string; alertThreshold: string };
}

const MODE_OPTIONS: readonly SegmentedOption<RechargeFeatureMode>[] = RECHARGE_FEATURE_MODES.map((mode) => ({
  value: mode,
  label: RECHARGE_FEATURE_MODE[mode].label,
}));

/** La remise renouvellement n'a pas de simulation (`07` §7) : Désactivé ou Actif. */
const RENEWAL_MODE_OPTIONS: readonly SegmentedOption<RechargeFeatureMode>[] = MODE_OPTIONS.filter((option) =>
  (RENEWAL_DISCOUNT_FEATURE_MODES as readonly RechargeFeatureMode[]).includes(option.value),
);

const CANTON_FORMAT_OPTIONS: readonly SegmentedOption<SwissCantonFormat>[] = [
  { value: 'name', label: 'Nom allemand' },
  { value: 'code', label: 'Code ISO' },
];

const MAX_ATTEMPTS_ID = 'recharge-max-attempts';
const MAX_ATTEMPTS_ERROR = 'Saisissez un nombre entier entre 1 et 20';
const MAX_ATTEMPTS_HINT = 'Recharge n’expose pas ce maximum : il sert à afficher « Tentative N/M »';
const RECONCILE_SCHEDULE_ID = 'recharge-renewal-reconcile-schedule';
const RECONCILE_SCHEDULE_ERROR = 'Saisissez une expression cron à 5 champs (minute heure jour mois jour-de-semaine)';
const RECONCILE_SCHEDULE_HINT = 'Heure de Paris, au format cron : « 0 6 * * * » = tous les jours à 6 h';
const ALERT_THRESHOLD_ID = 'recharge-renewal-alert-threshold';
const ALERT_THRESHOLD_ERROR = 'Saisissez un nombre entier entre 1 et 10 000';
const ALERT_THRESHOLD_HINT = 'Alerte quand une passe compte au moins ce nombre de remises perdues ou en erreur';
const TEST_MODE_NOTE = 'Le mode test de Flux force la simulation de toutes les fonctions (Paramètres → Avancé)';

const listFormat = new Intl.ListFormat('fr', { type: 'conjunction' });

function toDraft(features: RechargeFeatureSettings): FeaturesDraft {
  return {
    chargeFailed: { mode: features.chargeFailed.mode, maxAttempts: String(features.chargeFailed.maxAttempts) },
    swissCanton: { mode: features.swissCanton.mode, provinceFormat: features.swissCanton.provinceFormat },
    legacyMigration: { mode: features.legacyMigration.mode },
    renewalDiscount: {
      mode: features.renewalDiscount.mode,
      reconcileSchedule: features.renewalDiscount.reconcileSchedule,
      alertThreshold: String(features.renewalDiscount.alertThreshold),
    },
  };
}

/** Contrôle de forme seulement : le serveur valide l'expression (400 sinon). */
function parseSchedule(raw: string): string | null {
  const trimmed = raw.trim().replace(/\s+/g, ' ');
  return trimmed.split(' ').length === 5 ? trimmed : null;
}

function parseAlertThreshold(raw: string): number | null {
  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) {
    return null;
  }
  const value = Number(trimmed);
  return value >= 1 && value <= 10_000 ? value : null;
}

function parseMaxAttempts(raw: string): number | null {
  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) {
    return null;
  }
  const value = Number(trimmed);
  return value >= 1 && value <= 20 ? value : null;
}

/** Titre et corps de la confirmation de passage en Actif (`06` §8.6). */
export function activationCopy(features: readonly RechargeFeature[]): { title: string; description: string } {
  const labels = features.map((feature) => RECHARGE_FEATURE[feature].label);
  // La remise renouvellement (`07`) n'a jamais existé sur l'ancien serveur : rien à y supprimer.
  const topics = features.filter((feature) => feature !== 'renewalDiscount').flatMap((feature) => RECHARGE_FEATURE[feature].topics);
  const webhooks = topics.length === 1 ? `le webhook ${topics[0] ?? ''}` : `les webhooks ${listFormat.format(topics)}`;
  const cleanup = topics.length === 0 ? '' : ` Supprimez ensuite ${webhooks} de l’ancien serveur pour éviter les doublons.`;
  const renewal = features.includes('renewalDiscount')
    ? ' Les remises ne sont posées que pour les opérations « remise renouvellement » activées ; enregistrez le topic charge/created dans la carte Webhooks.'
    : '';
  return {
    title: `Activer ${listFormat.format(labels)} ?`,
    // Paiements échoués ne fait que lire Recharge (`06` §2) : ne pas annoncer d'écriture.
    description: `${
      features.every((feature) => feature === 'chargeFailed') ? 'Flux publiera sur Slack.' : 'Flux écrira chez Recharge et publiera sur Slack.'
    }${cleanup}${renewal}`,
  };
}

/**
 * Carte **Fonctions** (`06` §8.6, §0) : un mode par fonction (Désactivé ·
 * Simulation · Actif) et ses réglages propres, un seul enregistrement. Un
 * passage en Actif est confirmé, puisqu'il écrit chez Recharge et publie sur
 * Slack.
 */
export function RechargeFeaturesSection({ settings }: { settings: RechargeSettingsSummary }): JSX.Element {
  const [baseline, setBaseline] = useState(settings.features);
  const [draft, setDraft] = useState<FeaturesDraft>(() => toDraft(settings.features));
  const [maxAttemptsError, setMaxAttemptsError] = useState<string | undefined>(undefined);
  const [scheduleError, setScheduleError] = useState<string | undefined>(undefined);
  const [thresholdError, setThresholdError] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState<{ body: RechargeFeatureSettings; activating: RechargeFeature[] } | null>(
    null,
  );
  const save = useSaveRechargeSettings((summary) => {
    setDraft(toDraft(summary.features));
    setPending(null);
  });

  // Réglages relus depuis le serveur : on suit la nouvelle version tant que
  // le brouillon n'a pas été modifié, sans jamais écraser une saisie en cours.
  if (!isSameValue(baseline, settings.features)) {
    if (isSameValue(draft, toDraft(baseline))) {
      setDraft(toDraft(settings.features));
    }
    setBaseline(settings.features);
  }

  const isDirty = !isSameValue(draft, toDraft(settings.features));

  function setMode(feature: RechargeFeature, mode: RechargeFeatureMode): void {
    setDraft((prev) => ({ ...prev, [feature]: { ...prev[feature], mode } }));
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const maxAttempts = parseMaxAttempts(draft.chargeFailed.maxAttempts);
    if (maxAttempts === null) {
      setMaxAttemptsError(MAX_ATTEMPTS_ERROR);
      document.getElementById(MAX_ATTEMPTS_ID)?.focus();
      return;
    }
    const reconcileSchedule = parseSchedule(draft.renewalDiscount.reconcileSchedule);
    if (reconcileSchedule === null) {
      setScheduleError(RECONCILE_SCHEDULE_ERROR);
      document.getElementById(RECONCILE_SCHEDULE_ID)?.focus();
      return;
    }
    const alertThreshold = parseAlertThreshold(draft.renewalDiscount.alertThreshold);
    if (alertThreshold === null) {
      setThresholdError(ALERT_THRESHOLD_ERROR);
      document.getElementById(ALERT_THRESHOLD_ID)?.focus();
      return;
    }
    const body: RechargeFeatureSettings = {
      chargeFailed: { mode: draft.chargeFailed.mode, maxAttempts },
      swissCanton: { mode: draft.swissCanton.mode, provinceFormat: draft.swissCanton.provinceFormat },
      legacyMigration: { mode: draft.legacyMigration.mode },
      renewalDiscount: { mode: draft.renewalDiscount.mode === 'active' ? 'active' : 'disabled', reconcileSchedule, alertThreshold },
    };
    const activating = RECHARGE_FEATURES.filter(
      (feature) => body[feature].mode === 'active' && settings.features[feature].mode !== 'active',
    );
    if (activating.length > 0) {
      setPending({ body, activating });
      return;
    }
    save.mutate(body);
  }

  const confirmCopy = pending ? activationCopy(pending.activating) : null;

  return (
    <form onSubmit={handleSubmit} noValidate>
      <PageSection
        title="Fonctions"
        description="Chaque fonction traite ses webhooks selon son mode."
        footer={<SaveButton isDirty={isDirty} isLoading={save.isPending} />}
      >
        <div className="flex flex-col gap-5">
          {settings.testMode ? (
            <p className="flex items-start gap-2 rounded-md border border-status-orange-border bg-status-orange-bg px-3 py-2 text-sm text-status-orange-fg">
              <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              <span>{TEST_MODE_NOTE}</span>
            </p>
          ) : null}
          <div className="flex flex-col divide-y divide-border">
            {RECHARGE_FEATURES.map((feature) => {
              const meta = RECHARGE_FEATURE[feature];
              const mode = draft[feature].mode;
              const hintId = `recharge-${feature}-mode-hint`;
              return (
                <div key={feature} className="flex flex-col gap-3 py-5 first:pt-0 last:pb-0">
                  <div className="flex flex-col gap-0.5">
                    <h3 className="text-sm font-semibold">{meta.label}</h3>
                    <p className="text-xs text-muted-foreground">{meta.description}</p>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <SegmentedRadioGroup
                      name={`recharge-${feature}-mode`}
                      legend={`Mode : ${meta.label}`}
                      options={feature === 'renewalDiscount' ? RENEWAL_MODE_OPTIONS : MODE_OPTIONS}
                      value={mode}
                      disabled={settings.testMode}
                      describedBy={hintId}
                      onChange={(next) => {
                        setMode(feature, next);
                      }}
                    />
                    <p id={hintId} className="text-xs leading-5 text-muted-foreground">
                      {RECHARGE_FEATURE_MODE[mode].description}
                    </p>
                  </div>
                  {feature === 'chargeFailed' ? (
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor={MAX_ATTEMPTS_ID}>Nombre maximum de tentatives</Label>
                      <Input
                        id={MAX_ATTEMPTS_ID}
                        name={MAX_ATTEMPTS_ID}
                        type="number"
                        inputMode="numeric"
                        min={1}
                        max={20}
                        step={1}
                        className="w-24"
                        value={draft.chargeFailed.maxAttempts}
                        aria-invalid={maxAttemptsError ? true : undefined}
                        aria-describedby={describedByIds(MAX_ATTEMPTS_ID, MAX_ATTEMPTS_HINT, maxAttemptsError)}
                        onChange={(event) => {
                          const maxAttempts = event.target.value;
                          setMaxAttemptsError(undefined);
                          setDraft((prev) => ({ ...prev, chargeFailed: { ...prev.chargeFailed, maxAttempts } }));
                        }}
                      />
                      <FieldMessages id={MAX_ATTEMPTS_ID} hint={MAX_ATTEMPTS_HINT} error={maxAttemptsError} />
                    </div>
                  ) : null}
                  {feature === 'renewalDiscount' ? (
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="flex flex-col gap-1.5">
                        <Label htmlFor={RECONCILE_SCHEDULE_ID}>Réconciliation quotidienne</Label>
                        <Input
                          id={RECONCILE_SCHEDULE_ID}
                          name={RECONCILE_SCHEDULE_ID}
                          className="w-40 font-mono"
                          spellCheck={false}
                          autoComplete="off"
                          value={draft.renewalDiscount.reconcileSchedule}
                          aria-invalid={scheduleError ? true : undefined}
                          aria-describedby={describedByIds(RECONCILE_SCHEDULE_ID, RECONCILE_SCHEDULE_HINT, scheduleError)}
                          onChange={(event) => {
                            const reconcileSchedule = event.target.value;
                            setScheduleError(undefined);
                            setDraft((prev) => ({ ...prev, renewalDiscount: { ...prev.renewalDiscount, reconcileSchedule } }));
                          }}
                        />
                        <FieldMessages id={RECONCILE_SCHEDULE_ID} hint={RECONCILE_SCHEDULE_HINT} error={scheduleError} />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <Label htmlFor={ALERT_THRESHOLD_ID}>Seuil d’alerte</Label>
                        <Input
                          id={ALERT_THRESHOLD_ID}
                          name={ALERT_THRESHOLD_ID}
                          type="number"
                          inputMode="numeric"
                          min={1}
                          max={10_000}
                          step={1}
                          className="w-28"
                          value={draft.renewalDiscount.alertThreshold}
                          aria-invalid={thresholdError ? true : undefined}
                          aria-describedby={describedByIds(ALERT_THRESHOLD_ID, ALERT_THRESHOLD_HINT, thresholdError)}
                          onChange={(event) => {
                            const alertThreshold = event.target.value;
                            setThresholdError(undefined);
                            setDraft((prev) => ({ ...prev, renewalDiscount: { ...prev.renewalDiscount, alertThreshold } }));
                          }}
                        />
                        <FieldMessages id={ALERT_THRESHOLD_ID} hint={ALERT_THRESHOLD_HINT} error={thresholdError} />
                      </div>
                    </div>
                  ) : null}
                  {feature === 'swissCanton' ? (
                    <SegmentedRadioGroup
                      name="recharge-swiss-canton-format"
                      legend="Format du canton"
                      showLegend
                      options={CANTON_FORMAT_OPTIONS}
                      value={draft.swissCanton.provinceFormat}
                      onChange={(provinceFormat) => {
                        setDraft((prev) => ({ ...prev, swissCanton: { ...prev.swissCanton, provinceFormat } }));
                      }}
                    />
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      </PageSection>
      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open && !save.isPending) {
            setPending(null);
          }
        }}
        title={confirmCopy?.title ?? ''}
        description={confirmCopy?.description ?? ''}
        confirmLabel="Activer"
        confirmVariant="default"
        isConfirming={save.isPending}
        onConfirm={() => {
          if (pending) {
            save.mutate(pending.body);
          }
        }}
      />
    </form>
  );
}
